import React, { useEffect } from "react";
import { Mail, FileText } from "lucide-react";
import GlobalFooter from "../components/GlobalFooter";
import PublicLegalDocument from "../components/PublicLegalDocument";

export default function TermsOfService() {
  useEffect(() => {
    // Hide Emergent copilot on public Terms page
    const copilotWidget = document.querySelector('#emergent-copilot, [data-emergent-copilot], .emergent-chat-widget');
    if (copilotWidget) {
      copilotWidget.style.display = 'none';
    }
    
    return () => {
      if (copilotWidget) {
        copilotWidget.style.display = '';
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0A0A0E] to-black text-white flex flex-col">
      <div className="container mx-auto px-6 py-12 max-w-4xl flex-1">
        {/* Header */}
        <div className="mb-12 border-b border-white/10 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/30">
              <FileText size={32} className="text-pink-400" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Terms of Service
            </h1>
          </div>
          <p className="text-zinc-500">Last Updated: January 2026</p>
        </div>

        {/* Content */}
        <PublicLegalDocument docType="terms">
        <div className="space-y-8 prose prose-invert max-w-none">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-zinc-300 leading-relaxed">
              By accessing or using our services, you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Age Restriction</h2>
            <p className="text-zinc-300 leading-relaxed">
              You must be at least 18 years old to use our services. By using our services, you 
              represent and warrant that you are of legal age to form a binding contract. We take 
              age verification seriously and reserve the right to request proof of age at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. Account Registration</h2>
            <p className="text-zinc-300 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials. 
              You agree to notify us immediately of any unauthorized access to your account. You 
              are solely responsible for all activities that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Content Ownership & DRM Protection</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              All content provided through our services remains the intellectual property of the 
              content creator. Subscribers are granted a limited, non-exclusive, non-transferable 
              license to view content for personal use only. Our content is protected by DRM 
              (Digital Rights Management) technology.
            </p>
            <p className="text-zinc-300 leading-relaxed">
              Any attempt to circumvent DRM protection, download, record, or redistribute content 
              is strictly prohibited and may result in immediate account termination and legal action.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Prohibited Activities</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li>Download, copy, record, screenshot, or redistribute content without explicit permission</li>
              <li>Share your account credentials with others</li>
              <li>Use our services for any illegal purpose</li>
              <li>Harass, abuse, or harm other users or staff</li>
              <li>Attempt to circumvent any security features or DRM protection</li>
              <li>Use automated tools, bots, or scrapers to access our services</li>
              <li>Reverse engineer or decompile any part of our platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Payments and Refunds</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              All payments are processed securely through our trusted payment partners. For video purchases:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
              <li><strong>Direct Sale (Own Forever):</strong> One-time payment for permanent access to the video</li>
              <li><strong>PPV (Rent 48h):</strong> Lower-priced rental with 48-hour viewing window</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mt-4">
              All sales are final. Refunds may be considered on a case-by-case basis for technical 
              issues preventing content access. Contact us within 48 hours of purchase if you 
              experience playback issues.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Content Moderation & DMCA</h2>
            <p className="text-zinc-300 leading-relaxed">
              We respect intellectual property rights and comply with the Digital Millennium Copyright 
              Act (DMCA). If you believe content infringes your rights, please follow our takedown 
              procedure outlined in our Takedown Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Termination</h2>
            <p className="text-zinc-300 leading-relaxed">
              We reserve the right to suspend or terminate your account at any time for violation 
              of these terms, suspected fraudulent activity, or for any other reason at our sole 
              discretion. Upon termination, you lose all access to purchased content except where 
              legally required to provide continued access.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-zinc-300 leading-relaxed">
              Our services are provided "as is" without warranties of any kind, express or implied. 
              We shall not be liable for any indirect, incidental, special, consequential, or punitive 
              damages arising from your use of our services, including but not limited to loss of 
              profits, data, or other intangible losses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Governing Law & Jurisdiction</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              These Terms of Service shall be governed by and construed in accordance with the laws of 
              <strong className="text-zinc-100"> Greece</strong>. The exclusive jurisdiction for any disputes 
              arising from these terms or your use of our services shall be the courts of 
              <strong className="text-zinc-100"> Athens, Greece</strong>.
            </p>
            <p className="text-zinc-300 leading-relaxed">
              By using our services, you consent to the jurisdiction and venue of the courts located in 
              Athens, Greece for the resolution of any disputes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to Terms</h2>
            <p className="text-zinc-300 leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of 
              significant changes via email or through our services. Your continued use of the 
              platform after changes constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Contact Information</h2>
            <div className="backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-6">
              <p className="text-zinc-300 mb-4">
                For questions about these Terms of Service, please contact:
              </p>
              <a 
                href="mailto:privacy@giopilos.com"
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition font-semibold"
              >
                <Mail size={18} />
                privacy@giopilos.com
              </a>
              <p className="text-zinc-400 text-sm mt-4">
                GP Studio<br />
                Athens, Greece
              </p>
            </div>
          </section>
        </div>
        </PublicLegalDocument>
      </div>
      <GlobalFooter />
    </div>
  );
}
